import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Card } from '../components/ui';
import { Mail, Lock, User, Eye, EyeOff, Loader2, Palette, MapPin, Instagram, Globe, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, setUser } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '', email: '', password: '',
    studio_name: '', location: '', instagram: '', website: '', phone: '', styles: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError(t('auth.invalidEmail', 'Please enter a valid email'));
      return;
    }
    if (formData.password.length < 6) {
      setError(t('auth.passwordMin', 'Password must be at least 6 characters'));
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        navigate('/artist/dashboard');
      } else {
        if (!formData.name.trim()) {
          setError(t('auth.nameRequired', 'Name is required'));
          setLoading(false);
          return;
        }
        const response = await fetch(`${API_URL}/api/auth/register-artist`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            password: formData.password,
            studio_name: formData.studio_name,
            location: formData.location,
            instagram: formData.instagram,
            website: formData.website,
            phone: formData.phone,
            styles: formData.styles.split(',').map(s => s.trim()).filter(Boolean)
          }),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Registration failed');
        }

        const userData = await response.json();
        setUser(userData);
        navigate('/artist/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 hero-glow opacity-30" />
      <div
        className="absolute inset-0 opacity-10 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/955938/pexels-photo-955938.jpeg?auto=compress&w=1920')`
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg relative z-10"
      >
        <Link to="/" className="block text-center mb-6">
          <span className="font-heading text-3xl">
            INK<span className="text-primary">TRACK</span>
          </span>
          <span className="block text-sm text-primary font-heading mt-1">
            {t('artist.portal', 'ARTIST PORTAL')}
          </span>
        </Link>

        <Card className="p-8">
          {/* Tabs */}
          <div className="flex mb-6 border-b border-border">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 pb-3 text-sm font-heading tracking-wider border-b-2 transition-colors ${
                isLogin ? 'text-primary border-primary' : 'text-muted-foreground border-transparent'
              }`}
            >
              {t('artist.signIn', 'Artist Sign In')}
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 pb-3 text-sm font-heading tracking-wider border-b-2 transition-colors ${
                !isLogin ? 'text-primary border-primary' : 'text-muted-foreground border-transparent'
              }`}
            >
              {t('artist.register', 'Register as Artist')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input name="name" placeholder={t('artist.fullName', 'Full Name *')} value={formData.name} onChange={handleChange} className="pl-12" required />
                </div>
                <div className="relative">
                  <Palette className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input name="studio_name" placeholder={t('artist.studioName', 'Studio Name')} value={formData.studio_name} onChange={handleChange} className="pl-12" />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input name="location" placeholder={t('artist.location', 'City, Country')} value={formData.location} onChange={handleChange} className="pl-12" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input name="instagram" placeholder="@instagram" value={formData.instagram} onChange={handleChange} className="pl-12" />
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input name="phone" placeholder={t('artist.phone', 'Phone')} value={formData.phone} onChange={handleChange} className="pl-12" />
                  </div>
                </div>
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input name="website" placeholder="https://website.com" value={formData.website} onChange={handleChange} className="pl-12" />
                </div>
                <Input name="styles" placeholder={t('artist.styles', 'Styles (comma separated): Realism, Blackwork, Japanese...')} value={formData.styles} onChange={handleChange} />
              </>
            )}

            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input type="email" name="email" placeholder={t('auth.email', 'Email')} value={formData.email} onChange={handleChange} className="pl-12" required />
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder={t('auth.password', 'Password')}
                value={formData.password}
                onChange={handleChange}
                className="pl-12 pr-12"
                required
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                isLogin ? t('artist.signInBtn', 'Sign In as Artist') : t('artist.registerBtn', 'Create Artist Account')
              )}
            </Button>
          </form>
        </Card>

        <div className="text-center mt-6 space-y-2">
          <p className="text-sm text-muted-foreground">
            {t('artist.notArtist', 'Not an artist?')}{' '}
            <Link to="/login" className="text-primary hover:underline">
              {t('artist.userLogin', 'Sign in as user')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ArtistLogin;
